import { Fragment, useEffect } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrashAlt } from '@fortawesome/pro-regular-svg-icons'

import { AddressAutoCompleteFormInput } from '@commutifi-fe/commutifi-specific/Presentation/AddressAutocomplete'
import { Themes } from '@commutifi-fe/ui/components/theme/constants'
import { Form, Button, Question, SelectableCardGrid } from '@commutifi-fe/ui'
import { ValidationRules } from '@commutifi-fe/interfaces/thirdParty/antd'
import { AddressPoint } from '@commutifi-fe/interfaces'
import { LEG_TYPES } from '@commutifi-fe/constants'

import { DayPicker, DayPickerProps, getDayPickerValidation } from 'shared/components/common/DayPicker'
import { validateAddressDistance, validateAddressOption } from 'utils/helpers/address'
import { primaryRouteObjectId, secondaryRouteObjectId } from 'constants/onboarding'
import { FormattedMessage, IntlShape, useIntl } from 'locales/index'
import { Frequency, Route, RouteLeg } from 'shared/interfaces'
import { extractStreetFromGoogleAddress } from 'utils/routes'
import { isLastItem } from 'utils/helpers'
import { getArrayValue } from 'utils/index'
import {
  getTranslationKeyForCategory,
  getTranslationKeyForMode,
  getTranslationKeyFromTransport,
  inferModeFromLegs,
  typeSafeIsValidMode
} from 'utils/modes'
import { inferFrequencyInitialValueFrom } from './_utils'

export interface RouteWithFrequency extends Route {
  frequency?: Frequency
}

export interface IActivityMapperProps {
  // Antd form item id
  id?: string
  // All routes legs details ordered by importance (primary, secondary, ...)
  // NOTE: we only need a subset of legs with: mode, category, startAddress, endAddress
  routes: RouteWithFrequency[]
  // Parent question id
  questionId?: string
  defaultValidation?: ValidationRules
  // To skip the frequency question which will assume the primary route frequency
  // of if there is 2 routes to select from it will assume the combined frequency
  // DEFAULT: false
  skipFrequency?: boolean
  theme?: Themes | `${Themes}`
}

type WhichRouteProps = Omit<IActivityMapperProps, 'skipFrequency'>
type WhichLegProps = Omit<WhichRouteProps, 'skipFrequency' | 'form'>
type WhichFrequencyProps = Omit<WhichLegProps, 'defaultValidation'>
type AddressListProps = WhichFrequencyProps & { theme?: Themes | `${Themes}` }

export enum ActivityFields {
  Frequency = 'frequency',
  StopLocations = 'stopLocations',
  Keys = 'keys',
  Routes = 'routes'
}

export enum RouteOptions {
  Primary = 'primary',
  Secondary = 'secondary'
}

export interface ActivityMapperValueProp {
  [ActivityFields.Frequency]?: Frequency
  [ActivityFields.StopLocations]: AddressPoint[] | string[]
  [ActivityFields.Keys]?: number[]
  [ActivityFields.Routes]?: string[]
  [RouteOptions.Primary]?: { legIndex: string }
  [RouteOptions.Secondary]?: { legIndex: string }
}

type TargetedRouteType = 'primary' | 'secondary'
enum RouteIndexes {
  // eslint-disable-next-line @typescript-eslint/naming-convention -- Legacy reasons, some logic is based on lower case first letter ... (Ex. routes[RouteIndexes[routeOption]] where routeOptions is 'primary')
  primary = 0,
  // eslint-disable-next-line @typescript-eslint/naming-convention -- Legacy
  secondary = 1
}

export const getOrdinalTranslation = (index: number, intl: IntlShape): JSX.Element => {
  switch (index) {
    case 0:
      return <FormattedMessage id="global.ordinal.first" />
    case 1:
      return <FormattedMessage id="global.ordinal.second" />
    case 2:
      return <FormattedMessage id="global.ordinal.third" />

    default:
      return <FormattedMessage id="global.ordinal.nth" values={{ rank: intl.formatNumber(index + 1) }} />
  }
}

/**
 * Generic way to process text to describe legs of a routes and that makes
 * sense in english AND french.
 *
 * @param intl - Intl object to translate contents
 * @param legs - Full legs of a route to extract different addresses and modes
 *
 * @returns String: When traveling by \{mode\} until \{address (shorter version)\}
 *                    and traveling by walk until work (last address : work)
 */
const generateLegsDescription = (intl: IntlShape, legs: RouteLeg[]): string => {
  const knownAddressLegs = legs.filter((leg) => leg.endAddress && leg.type === LEG_TYPES.leg)

  // If we filtered all legs but one because we are missing end addresses we probably
  // want to summarize the route with the inferred mode
  if (legs.length !== knownAddressLegs.length && knownAddressLegs.length === 1) {
    const formattedAddress = extractStreetFromGoogleAddress(knownAddressLegs[0].endAddress)
    const formattedMode = intl
      .formatMessage({ id: getTranslationKeyFromTransport(inferModeFromLegs(legs)) })
      .toLowerCase()
    return intl.formatMessage(
      { id: 'activities.questions.kids.where.description.start' },
      { mode: formattedMode, address: formattedAddress }
    )
  }

  let description = ''
  knownAddressLegs.forEach((leg, i) => {
    const formattedMode = intl.formatMessage({ id: getTranslationKeyFromTransport(leg) }).toLowerCase()
    const formattedAddress = extractStreetFromGoogleAddress(leg.endAddress)
    if (i === 0) {
      description = intl.formatMessage(
        { id: 'activities.questions.kids.where.description.start' },
        { mode: formattedMode, address: formattedAddress }
      )
    } else if (isLastItem(i, knownAddressLegs.length)) {
      description = `${description} ${intl.formatMessage(
        { id: 'activities.questions.kids.where.description.end' },
        { mode: formattedMode }
      )}`
    } else {
      description = `${description}, ${intl.formatMessage(
        { id: 'activities.questions.kids.where.description.middle' },
        { mode: formattedMode, address: formattedAddress }
      )}`
    }
  })
  return description
}

/**
 * Extract first leg start location and last leg end location to represent
 * the route starting and ending points
 * @param legs - Legs of a route
 */
const getEndpointsLocations = (legs: RouteLeg[]) => [legs[0].startLocation, legs[legs.length - 1].endLocation]

/**
 * Create a sub question if based on the optional parent question id
 * @param parentId - Parent id to create sub question
 * @param fieldName - Name of the field we want to add to the form
 */
const getSubQuestionId = (parentId: string | undefined, fieldName: string | string[]): string | string[] =>
  parentId ? [parentId, ...getArrayValue(fieldName)] : fieldName

function WhichRoute({ questionId, defaultValidation, routes }: WhichRouteProps) {
  const intl = useIntl()
  return (
    <Form.Item shouldUpdate noStyle>
      {({ setFieldsValue }) => {
        const handleSelectedRoutesChange = (routeOptions: TargetedRouteType[] | null) => {
          if (!routeOptions?.length) {
            return
          }
          // More than one route selected -> we don't set any frequency because
          // we assume the user picks up his kids on each day of each route
          if (routeOptions.length > 1) {
            const frequencyUpdatedValue = { frequency: undefined }
            return questionId
              ? setFieldsValue({ [questionId]: frequencyUpdatedValue })
              : setFieldsValue(frequencyUpdatedValue)
          }

          // There should never be more routeOptions than the number of routes
          const routeIndex = RouteIndexes[routeOptions[0]]
          const frequency = routeOptions.length ? routes[routeIndex]?.frequency : routes[0].frequency
          const availableFrequency = inferFrequencyInitialValueFrom(frequency, { presetDays: true })
          const frequencyUpdatedValue = { frequency: availableFrequency }
          questionId ? setFieldsValue({ [questionId]: frequencyUpdatedValue }) : setFieldsValue(frequencyUpdatedValue)
        }
        return routes.length > 1 ? (
          <Form.Item
            name={getSubQuestionId(questionId, ActivityFields.Routes)}
            valuePropName="selectedCards"
            {...defaultValidation}
            label={
              <Question
                title={<FormattedMessage id="activities.questions.kids.where.whichRoute" />}
                hint={<FormattedMessage id="activities.questions.kids.where.whichRoute.hint" />}
              />
            }
            data-testid="commute-activity-which-route"
          >
            <SelectableCardGrid multiSelect baseWidth="auto" onChange={handleSelectedRoutesChange}>
              <SelectableCardGrid.ClickableCard
                id={primaryRouteObjectId}
                title={
                  <FormattedMessage
                    id={getTranslationKeyForCategory(inferModeFromLegs(routes[0].legs).category).title}
                  />
                }
                description={generateLegsDescription(intl, routes[0].legs || [])}
                justify="start"
              />
              <SelectableCardGrid.ClickableCard
                id={secondaryRouteObjectId}
                title={
                  <FormattedMessage
                    id={getTranslationKeyForCategory(inferModeFromLegs(routes[1].legs).category).title}
                  />
                }
                description={generateLegsDescription(intl, routes[1].legs || [])}
                justify="start"
              />
            </SelectableCardGrid>
          </Form.Item>
        ) : null
      }}
    </Form.Item>
  )
}

function WhichFrequency({ questionId, routes }: WhichFrequencyProps) {
  return (
    <Form.Item shouldUpdate noStyle>
      {({ getFieldValue }) => {
        // Set initial day picker value based on the user commute frequency if
        // he already answered the question about commute frequency.
        const whichRouteValue: TargetedRouteType[] | undefined = getFieldValue(
          getSubQuestionId(questionId, ActivityFields.Routes)
        )

        let dayPickerProps: Partial<DayPickerProps> = { initialValue: undefined }
        if (!whichRouteValue || whichRouteValue.length <= 1) {
          // When the user has only one route in which he potentially picks up his kids, we preselect the days
          // to be the same as the route.
          // Also, we always disable the options that are not in the route that the user selected to indicate
          // he picks up his kids
          const targetRouteIndex = whichRouteValue?.[0] ? RouteIndexes[whichRouteValue[0]] : 0
          const frequency = routes[targetRouteIndex]?.frequency
          const availableFrequency = inferFrequencyInitialValueFrom(frequency, { presetDays: true })

          dayPickerProps = {
            daysSubset: frequency?.days,
            maxWeeklyFrequency: availableFrequency?.weeklyFrequency,
            setDays: Boolean(frequency?.days)
          }
        }

        return (
          (whichRouteValue?.length === 1 || routes.length === 1) && (
            <Form.Item
              name={getSubQuestionId(questionId, ActivityFields.Frequency)}
              {...getDayPickerValidation(1, dayPickerProps.maxWeeklyFrequency)}
              label={<Question title={<FormattedMessage id="activities.questions.kids.frequency" />} />}
              data-testid="commute-activity-frequency"
            >
              <DayPicker
                key={dayPickerProps.daysSubset ? dayPickerProps.daysSubset.join(',') : 'frequency_key'}
                {...dayPickerProps}
              />
            </Form.Item>
          )
        )
      }}
    </Form.Item>
  )
}

function WhichLeg({ questionId, routes, defaultValidation }: WhichLegProps) {
  const intl = useIntl()
  return (
    <Form.Item shouldUpdate noStyle>
      {({ getFieldValue }) => {
        const routesValue = getFieldValue(getSubQuestionId(questionId, ActivityFields.Routes))
        return (routesValue || (routes.length === 1 ? [primaryRouteObjectId] : []))?.map(
          (routeOption: TargetedRouteType) => {
            const routeLegs = (routes[RouteIndexes[routeOption]]?.legs || []).filter(
              (leg) => leg.type === LEG_TYPES.leg
            )
            // Ask on which leg only when there is multiple legs
            return (
              routeLegs.length > 1 && (
                <Form.Item
                  key={routeOption}
                  name={getSubQuestionId(questionId, [routeOption, 'legIndex'])}
                  valuePropName="selectedCards"
                  label={
                    <Question
                      title={`${generateLegsDescription(intl, routeLegs)}, ${intl
                        .formatMessage({ id: 'activities.questions.kids.where.whichLeg' })
                        .toLowerCase()}`}
                    />
                  }
                  data-testid="commute-activity-which-leg"
                  {...defaultValidation}
                >
                  <SelectableCardGrid className="u-margin-top--small">
                    {routeLegs.map((leg, i) => {
                      const translationKey = typeSafeIsValidMode(leg.mode || '')
                        ? getTranslationKeyForMode(leg.mode || '')
                        : getTranslationKeyForCategory(leg.category).title
                      const translatedMode = intl.formatMessage({ id: translationKey }).toLowerCase()
                      return (
                        <SelectableCardGrid.ClickableCard
                          key={i}
                          id={`${i}`}
                          title={
                            // If, for example we call google to get the public transit details, we won't have
                            // the end address of each leg. In that case we will not show the `until ${leg.endAddress}` part
                            leg.endAddress
                              ? intl.formatMessage(
                                  { id: 'activities.questions.kids.where.description.start' },
                                  {
                                    mode: translatedMode,
                                    address: extractStreetFromGoogleAddress(leg.endAddress)
                                  }
                                )
                              : intl.formatMessage(
                                  { id: 'activities.questions.kids.where.description.start.unknownEndAddress' },
                                  { mode: translatedMode }
                                )
                          }
                        />
                      )
                    })}
                  </SelectableCardGrid>
                </Form.Item>
              )
            )
          }
        )
      }}
    </Form.Item>
  )
}

function AddressList({ questionId, routes, theme }: AddressListProps) {
  const intl = useIntl()

  return (
    <Form.List name={getSubQuestionId(questionId, ActivityFields.StopLocations)}>
      {(fields, { add, remove }) => (
        <>
          {fields.map(({ key, name }, index) => {
            const [primaryRouteStartLocation, primaryRouteEndLocation] = getEndpointsLocations(routes[0].legs || [])
            return (
              <Fragment key={key}>
                <Form.Item
                  data-testid={`activity-address-${index + 1}`}
                  name={[name]}
                  rules={[
                    {
                      validator: (rule: any, options: any) => validateAddressOption(rule, options, { intl })
                    },
                    {
                      validator: (rule: any, options: any) =>
                        validateAddressDistance(rule, options, {
                          officeLocation: primaryRouteStartLocation || primaryRouteEndLocation,
                          intl
                        })
                    }
                  ]}
                  label={
                    <Question
                      title={
                        <FormattedMessage
                          id="activities.questions.kids.where"
                          values={{ ordinal: getOrdinalTranslation(index, intl) }}
                        />
                      }
                      addOn={
                        index !== 0 && (
                          <Button type="link" style={{ padding: 0 }} onClick={() => remove(name)}>
                            <FontAwesomeIcon className="u-color--accent-red-400" icon={faTrashAlt} />
                          </Button>
                        )
                      }
                    />
                  }
                >
                  <AddressAutoCompleteFormInput
                    placeholder={intl.formatMessage({ id: 'activities.questions.kids.where.placeholder' })}
                  >
                    {/* Primary route and secondary routes should have the same start and end locations for now
                     since the user only has one home and one office. Thus, we can use the primary route as location markers */}
                    <AddressAutoCompleteFormInput.Map
                      startLocation={primaryRouteStartLocation}
                      endLocation={primaryRouteEndLocation}
                      theme={theme}
                    />
                  </AddressAutoCompleteFormInput>
                </Form.Item>
                {!isLastItem(index, fields.length) && <div className="u-margin-top--large-2" />}
              </Fragment>
            )
          })}
          <Button type="link" style={{ padding: 0 }} onClick={() => add()}>
            <FormattedMessage id="activities.dropOffKids.button.add" />
          </Button>
        </>
      )}
    </Form.List>
  )
}

export function ActivityMapper({
  id,
  questionId,
  defaultValidation,
  routes,
  skipFrequency = false,
  theme
}: IActivityMapperProps) {
  useEffect(() => {
    // Validate parameters
    if (routes.length < 1) {
      throw Error('Please pass at least one route to ActivityMapper')
    }
  }, [routes])

  return (
    <span id={id}>
      <WhichRoute questionId={questionId} routes={routes} defaultValidation={defaultValidation} />
      {/* To simplify, if the user selects that he drops his kids on his 2 routes we assume he drops
       them on each day of both routes (That way we don't create 2 extra commute profiles) */}
      {!skipFrequency && <WhichFrequency questionId={questionId} routes={routes} />}
      <WhichLeg questionId={questionId} routes={routes} defaultValidation={defaultValidation} />
      <AddressList questionId={questionId} routes={routes} theme={theme} />
    </span>
  )
}
